import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import Modal from '../../components/Modal';
import {
    createMaterialCategory,
    deleteMaterialCategory,
    listMaterialCategories,
    MaterialCategory,
    MaterialCategoryNode,
    reorderMaterialCategories,
    updateMaterialCategory,
} from '../../api/materialCategory';
import { alertApprovalIfPending, ApiError, imgSrc } from '../../api/request';
import { invalidateCategoryTreeCache } from '../../components/MaterialFieldInputs';
import ImageUploader from '../../components/ImageUploader';
import CheckPickList from '../../components/CheckPickList';
import { listAllDevices } from '../../api/device';
import BatchActions, {
    HeaderCheckbox,
    RowCheckbox,
} from '../../components/BatchActions';
import { useBatchSelect, BatchSelect } from '../../utils/useBatchSelect';
import { confirmDialog, alertDialog } from '../../utils/dialog';
import { useCan } from '../../store';
import { useIsMaintainer } from '../../utils/readonlyScope';

// 材料类别管理. 主从双栏布局:
// 左栏 = 一级分类表, 点行选中; 右栏 = 选中一级分类下的二级分类表.
// 材料主数据的 类别1/类别2 只能来源于这里维护的启用项 (后端强校验).
// 登录即可操作: admin 写操作直接生效; 普通用户提交后走审批工单, 通过后才生效.

interface EditorState {
    /** null = 新增; 否则为被编辑项 */
    target: MaterialCategory | null;
    parentId: number;
    /** 类别编码 (业务键, 建后不可改): 新增时默认跟随英文名, 手动改过后不再跟随 */
    categoryCode: string;
    codeTouched: boolean;
    nameZh: string;
    nameEn: string;
    icon: string;
    codePrefix: string;
    /** 材料使用说明链接 (仅一级分类维护) */
    usageDocUrl: string;
    /** 使用说明展示标题 (与链接成对) */
    usageDocTitle: string;
    /** 剔除机型 (仅二级分类维护) */
    excludedDeviceFamilies: string[];
}

/** 剔除机型候选: 一个机型码 (device.family) 一行, 附该机型下的设备名 */
interface FamilyOption {
    family: string;
    names: string[];
}

/**
 * 剔除机型单元格: 悬停弹出完整机型清单 (机型码 + 设备名).
 * 表格外层是 overflow-x-auto, 绝对定位的气泡会被裁掉 → portal 到 body 按单元格位置 fixed 定位.
 */
function ExcludedFamiliesCell({
    families,
    names,
    onHover,
}: {
    families: string[];
    /** 机型码 → 设备名 (候选未加载时为空, 只显示机型码) */
    names: Map<string, string[]>;
    onHover: () => void;
}) {
    const [anchor, setAnchor] = useState<DOMRect | null>(null);
    const label =
        families.length > 3
            ? `${families.length} 个机型`
            : families.join('、');
    // 机型多 (>12) 分两列, 不截断; 按行数估高, 下方放不下就往上弹
    const twoCols = families.length > 12;
    const width = twoCols ? 460 : 256;
    const height = 40 + Math.ceil(families.length / (twoCols ? 2 : 1)) * 18;
    const placeAbove = anchor
        ? anchor.bottom + height + 8 > window.innerHeight &&
          anchor.top > height + 8
        : false;
    return (
        <>
            <span
                className="block max-w-[160px] truncate font-mono text-[11px] text-amber-700 cursor-help underline decoration-dotted underline-offset-2"
                onMouseEnter={(e) => {
                    onHover();
                    setAnchor(e.currentTarget.getBoundingClientRect());
                }}
                onMouseLeave={() => setAnchor(null)}
            >
                {label}
            </span>
            {anchor &&
                createPortal(
                    <div
                        className="pointer-events-none fixed z-[1000] rounded-lg bg-slate-800 px-3 py-2 text-xs text-white shadow-lg"
                        style={{
                            width,
                            left: Math.max(
                                8,
                                Math.min(anchor.left, window.innerWidth - width - 8),
                            ),
                            ...(placeAbove
                                ? { bottom: window.innerHeight - anchor.top + 6 }
                                : { top: anchor.bottom + 6 }),
                        }}
                    >
                        <div className="mb-1 text-[11px] text-slate-300">
                            剔除机型 ({families.length}) · 这些机型不展示本分类
                        </div>
                        <div
                            className={`grid gap-x-4 gap-y-0.5 ${twoCols ? 'grid-cols-2' : 'grid-cols-1'}`}
                        >
                            {families.map((f) => (
                                <div
                                    key={f}
                                    className="flex min-w-0 gap-2 whitespace-nowrap"
                                >
                                    <span className="font-mono">{f}</span>
                                    <span className="truncate text-slate-400">
                                        {names.size
                                            ? (names.get(f)?.join(' / ') ??
                                              '平台未接入')
                                            : ''}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>,
                    document.body,
                )}
        </>
    );
}

function Toggle({
    on,
    onClick,
    title,
}: {
    on: boolean;
    onClick: () => void;
    title?: string;
}) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={on}
            title={title}
            onClick={onClick}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                on ? 'bg-brand-600' : 'bg-slate-300'
            }`}
        >
            <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    on ? 'translate-x-4' : 'translate-x-0.5'
                }`}
            />
        </button>
    );
}

function CategoryAdmin() {
    const canCreate = useCan('category', 'create');
    const canUpdate = useCan('category', 'update');
    const canDelete = useCan('category', 'delete');
    // 只读访问者只看在用类别 (停用项是维护过程态)
    const maintainer = useIsMaintainer('category');
    const [tree, setTree] = useState<MaterialCategoryNode[]>([]);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [editor, setEditor] = useState<EditorState | null>(null);
    const [busy, setBusy] = useState(false);
    const [sorting, setSorting] = useState(false);
    // 剔除机型候选: 全部设备 (含未发布) 按机型归并, 打开编辑框时懒加载一次
    const [families, setFamilies] = useState<FamilyOption[] | null>(null);
    const [familiesLoading, setFamiliesLoading] = useState(false);

    // 批量操作 (2026-09-10): 一级 / 二级各一套选择 —— 两张表是不同层级的删除范围,
    // 混在一个选中集合里, 用户很容易连带删掉整棵子树而不自知
    const cat1Sel = useBatchSelect(
        useMemo(() => tree.map((c) => String(c.id)), [tree]),
        String(tree.length),
    );
    const cat2Rows = useMemo(
        () => tree.find((c) => c.id === selectedId)?.children || [],
        [tree, selectedId],
    );
    const cat2Sel = useBatchSelect(
        useMemo(() => cat2Rows.map((c) => String(c.id)), [cat2Rows]),
        String(selectedId),
    );

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await listMaterialCategories({ includeDisabled: true });
            setTree(res || []);
        } catch (e) {
            const msg =
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            setError(msg);
            setTree([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // 默认选中第一个一级分类; 选中项被删除后回退到第一个.
    useEffect(() => {
        if (tree.length === 0) {
            setSelectedId(null);
            return;
        }
        if (selectedId === null || !tree.some((t) => t.id === selectedId)) {
            setSelectedId(tree[0].id);
        }
    }, [tree, selectedId]);

    const selected = tree.find((t) => t.id === selectedId) || null;

    const alertErr = (prefix: string, e: unknown) => {
        const msg =
            e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
        alertDialog(`${prefix}: ${msg}`);
    };

    // 同级分组内移动一行 (↑/↓/置顶), 整组依当前顺序落库 sno=1..N (一级/各一级下的二级各自成组).
    // 先本地生效, 落库后回读 (走审批流时数据未变, load 只是回读原值).
    const move = async (
        rows: MaterialCategory[],
        c: MaterialCategory,
        dir: -1 | 0 | 1,
    ) => {
        const idx = rows.findIndex((r) => r.id === c.id);
        if (idx < 0) return;
        const next = [...rows];
        const [row] = next.splice(idx, 1);
        if (dir === 0) {
            next.unshift(row); // 置顶
        } else {
            next.splice(Math.min(Math.max(idx + dir, 0), next.length), 0, row);
        }
        setTree((cur) =>
            c.parentId === 0
                ? (next as MaterialCategoryNode[])
                : cur.map((t) =>
                      t.id === c.parentId ? { ...t, children: next } : t,
                  ),
        );
        setSorting(true);
        try {
            const res = await reorderMaterialCategories(
                c.parentId,
                next.map((r) => r.id),
            );
            invalidateCategoryTreeCache();
            await load();
            alertApprovalIfPending(res.message);
        } catch (e) {
            alertErr('排序保存失败', e);
            await load();
        } finally {
            setSorting(false);
        }
    };

    const onToggle = async (c: MaterialCategory) => {
        try {
            const res = await updateMaterialCategory(c.id, {
                isEnabled: !c.isEnabled,
            });
            invalidateCategoryTreeCache();
            // 走审批流时数据未变, load 只是回读原值
            await load();
            alertApprovalIfPending(res.message);
        } catch (e) {
            alertErr('切换失败', e);
        }
    };

    const onDelete = async (c: MaterialCategory) => {
        if (!(await confirmDialog(`确认删除类别 ${c.nameZh} / ${c.nameEn}?`)))
            return;
        try {
            const res = await deleteMaterialCategory(c.id);
            invalidateCategoryTreeCache();
            await load();
            alertApprovalIfPending(res.message);
        } catch (e) {
            alertErr('删除失败', e);
        }
    };

    const onSaveEditor = async () => {
        if (!editor) return;
        if (!editor.categoryCode.trim() || !editor.nameZh.trim() || !editor.nameEn.trim()) {
            alertDialog('类别编码 / 中文名 / 英文名 都必填');
            return;
        }
        setBusy(true);
        try {
            let message: string;
            if (editor.target === null) {
                // 新增: sno 取当前同级最大值 + 10, 排到末尾
                const siblings =
                    editor.parentId === 0 ? tree : selected?.children || [];
                const sno = Math.max(0, ...siblings.map((s) => s.sno)) + 10;
                const res = await createMaterialCategory({
                    parentId: editor.parentId,
                    categoryCode: editor.categoryCode.trim(),
                    nameZh: editor.nameZh.trim(),
                    nameEn: editor.nameEn.trim(),
                    icon: editor.icon.trim() || undefined,
                    codePrefix: editor.codePrefix.trim() || undefined,
                    usageDocUrl: editor.usageDocUrl.trim() || undefined,
                    usageDocTitle: editor.usageDocTitle.trim() || undefined,
                    excludedDeviceFamilies:
                        editor.parentId > 0
                            ? editor.excludedDeviceFamilies
                            : undefined,
                    sno,
                });
                message = res.message;
            } else {
                const res = await updateMaterialCategory(editor.target.id, {
                    nameZh: editor.nameZh.trim(),
                    nameEn: editor.nameEn.trim(),
                    icon: editor.icon.trim(),
                    codePrefix: editor.codePrefix.trim(),
                    usageDocUrl: editor.usageDocUrl.trim(),
                    usageDocTitle: editor.usageDocTitle.trim(),
                    ...(editor.parentId > 0
                        ? {
                              excludedDeviceFamilies:
                                  editor.excludedDeviceFamilies,
                          }
                        : {}),
                });
                message = res.message;
            }
            setEditor(null);
            invalidateCategoryTreeCache();
            // 走审批流时数据未变, load 只是回读原值
            await load();
            alertApprovalIfPending(message);
        } catch (e) {
            alertErr('保存失败', e);
        } finally {
            setBusy(false);
        }
    };

    const ensureFamilies = async () => {
        if (families || familiesLoading) return;
        setFamiliesLoading(true);
        try {
            const byFamily = new Map<string, Set<string>>();
            (await listAllDevices()).forEach((d) => {
                const fam = d.family || d.deviceKey;
                if (!byFamily.has(fam)) byFamily.set(fam, new Set());
                if (d.name) byFamily.get(fam)!.add(d.name);
            });
            setFamilies(
                [...byFamily.entries()]
                    .map(([family, names]) => ({ family, names: [...names] }))
                    .sort((a, b) => a.family.localeCompare(b.family)),
            );
        } catch (e) {
            alertErr('机型列表加载失败', e);
        } finally {
            setFamiliesLoading(false);
        }
    };

    const familyNames = useMemo(
        () => new Map((families || []).map((f) => [f.family, f.names])),
        [families],
    );

    // 候选 = 平台机型 ∪ 已配置值 (老 CMS 搬来的机型码可能平台尚未接入, 也要能看到/取消)
    const familyOptions = useMemo(() => {
        const list = families || [];
        const known = new Set(list.map((f) => f.family));
        const extra = (editor?.excludedDeviceFamilies || [])
            .filter((f) => !known.has(f))
            .map((family) => ({ family, names: [] as string[] }));
        return [...list, ...extra];
    }, [families, editor?.excludedDeviceFamilies]);

    const openCreate = (parentId: number) => {
        if (parentId > 0) ensureFamilies();
        setEditor({
            target: null,
            parentId,
            categoryCode: '',
            codeTouched: false,
            nameZh: '',
            nameEn: '',
            icon: '',
            codePrefix: '',
            usageDocUrl: '',
            usageDocTitle: '',
            excludedDeviceFamilies: [],
        });
    };
    const openEdit = (c: MaterialCategory) => {
        if (c.parentId > 0) ensureFamilies();
        setEditor({
            target: c,
            parentId: c.parentId,
            categoryCode: c.categoryCode,
            codeTouched: true,
            nameZh: c.nameZh,
            nameEn: c.nameEn,
            icon: c.icon || '',
            codePrefix: c.codePrefix || '',
            usageDocUrl: c.usageDocUrl || '',
            usageDocTitle: c.usageDocTitle || '',
            excludedDeviceFamilies: c.excludedDeviceFamilies || [],
        });
    };

    const renderTable = (
        allRows: MaterialCategory[],
        opts: { selectable?: boolean; emptyHint: string; sel: BatchSelect },
    ) => {
        // 只读访问者只看在用类别 (停用项是维护过程态, 展示出来会被当成可选值)
        const rows = maintainer ? allRows : allRows.filter((r) => r.isEnabled);
        return (
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-slate-50">
                        <tr>
                            <th className="th w-8" hidden={!canDelete}>
                                <HeaderCheckbox
                                    hidden={!canDelete}
                                    sel={opts.sel}
                                />
                            </th>
                            <th className="th">中文名</th>
                            <th className="th">类别编码</th>
                            <th className="th">英文名</th>
                            {opts.selectable && (
                                <th
                                    className="th"
                                    title="本类材料的默认使用说明链接; 材料详情里单独配了的以材料为准"
                                >
                                    使用说明
                                </th>
                            )}
                            {!opts.selectable && (
                                <th
                                    className="th"
                                    title="材-02: 新建材料选中本类别后按 前缀+顺延编号 自动生成材质编码; 点「编辑」配置"
                                >
                                    编码前缀
                                </th>
                            )}
                            {!opts.selectable && (
                                <th
                                    className="th"
                                    title="这些机型的 Studio 材料库侧栏不展示本二级分类; 空 = 全机型可见"
                                >
                                    剔除机型
                                </th>
                            )}
                            <th className="th">图标</th>
                            <th className="th">状态</th>
                            <th
                                className="th"
                                hidden={!canUpdate && !canDelete}
                            >
                                操作
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td
                                    className="td text-center text-slate-400"
                                    colSpan={8}
                                >
                                    {opts.emptyHint}
                                </td>
                            </tr>
                        )}
                        {rows.map((c, i) => (
                            <tr
                                key={c.id}
                                className={
                                    opts.selectable
                                        ? `cursor-pointer ${
                                              c.id === selectedId
                                                  ? 'bg-brand-50/60'
                                                  : 'hover:bg-slate-50'
                                          }`
                                        : 'hover:bg-slate-50'
                                }
                                onClick={
                                    opts.selectable
                                        ? () => setSelectedId(c.id)
                                        : undefined
                                }
                            >
                                <td className="td">
                                    <RowCheckbox
                                        hidden={!canDelete}
                                        sel={opts.sel}
                                        k={String(c.id)}
                                    />
                                </td>
                                <td className="td whitespace-nowrap">
                                    {c.nameZh}
                                    {opts.selectable && (
                                        <span className="ml-1.5 text-[11px] text-slate-400">
                                            (
                                            {(c as MaterialCategoryNode)
                                                .children?.length ?? 0}
                                            )
                                        </span>
                                    )}
                                </td>
                                <td className="td font-mono text-[11px]">
                                    {c.categoryCode}
                                </td>
                                <td className="td font-mono text-[11px]">
                                    {c.nameEn}
                                </td>
                                {opts.selectable && (
                                    <td
                                        className="td text-xs"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {c.usageDocUrl ? (
                                            <a
                                                href={c.usageDocUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-brand-600 hover:underline block max-w-[110px] break-words"
                                                title={`${c.usageDocTitle || '未填标题'}\n${c.usageDocUrl}`}
                                            >
                                                {c.usageDocTitle || '查看'}
                                            </a>
                                        ) : (
                                            <span
                                                className="text-slate-300"
                                                title="未配置; 点「编辑」补"
                                            >
                                                —
                                            </span>
                                        )}
                                    </td>
                                )}
                                {!opts.selectable && (
                                    <td className="td font-mono text-xs">
                                        {c.codePrefix ? (
                                            <span className="tag bg-emerald-50 text-emerald-700">
                                                {c.codePrefix}
                                            </span>
                                        ) : (
                                            <span
                                                className="text-slate-300"
                                                title="未配置, 新建材料不会自动生成编码; 点「编辑」补"
                                            >
                                                —
                                            </span>
                                        )}
                                    </td>
                                )}
                                {!opts.selectable && (
                                    <td className="td text-xs">
                                        {c.excludedDeviceFamilies?.length ? (
                                            <ExcludedFamiliesCell
                                                families={c.excludedDeviceFamilies}
                                                names={familyNames}
                                                onHover={ensureFamilies}
                                            />
                                        ) : (
                                            <span className="text-slate-400">
                                                全机型
                                            </span>
                                        )}
                                    </td>
                                )}
                                <td className="td">
                                    {c.icon ? (
                                        <img
                                            src={imgSrc(c.icon)}
                                            alt={c.nameZh}
                                            className="h-8 w-8 rounded-full border border-slate-200 object-cover"
                                            onError={(e) => {
                                                (
                                                    e.currentTarget as HTMLImageElement
                                                ).style.display = 'none';
                                            }}
                                        />
                                    ) : (
                                        <span className="text-slate-300 text-xs">
                                            —
                                        </span>
                                    )}
                                </td>
                                <td
                                    className="td whitespace-nowrap"
                                    hidden={!maintainer}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {canUpdate ? (
                                        <Toggle
                                            on={c.isEnabled}
                                            onClick={() => onToggle(c)}
                                            title={
                                                c.isEnabled
                                                    ? '点击停用'
                                                    : '点击启用'
                                            }
                                        />
                                    ) : (
                                        <span
                                            className={`tag ${c.isEnabled ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}
                                        >
                                            {c.isEnabled ? '启用' : '停用'}
                                        </span>
                                    )}
                                </td>
                                <td
                                    className="td whitespace-nowrap pr-4"
                                    onClick={(e) => e.stopPropagation()}
                                    hidden={!canUpdate && !canDelete}
                                >
                                    <button
                                        type="button"
                                        className="btn-ghost px-1 py-0.5 text-xs disabled:opacity-30"
                                        title="上移"
                                        disabled={sorting || i === 0}
                                        onClick={() => move(rows, c, -1)}
                                        hidden={!canUpdate}
                                    >
                                        ↑
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-ghost px-1 py-0.5 text-xs disabled:opacity-30"
                                        title="下移"
                                        disabled={
                                            sorting || i === rows.length - 1
                                        }
                                        onClick={() => move(rows, c, 1)}
                                        hidden={!canUpdate}
                                    >
                                        ↓
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-ghost px-1 py-0.5 text-xs disabled:opacity-30 mr-1.5"
                                        title="置顶"
                                        disabled={sorting || i === 0}
                                        onClick={() => move(rows, c, 0)}
                                        hidden={!canUpdate}
                                    >
                                        ⤒
                                    </button>
                                    <button
                                        type="button"
                                        className="text-xs text-brand-600 hover:underline mr-2"
                                        onClick={() => openEdit(c)}
                                        hidden={!canUpdate}
                                    >
                                        编辑
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-danger-text"
                                        onClick={() => onDelete(c)}
                                        hidden={!canDelete}
                                    >
                                        删除
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="space-y-4">
            <div>
                <div className="text-base font-semibold">材料类别管理</div>
                <div className="text-xs text-slate-500">
                    材料主数据的 类别1 / 类别2 只能从这里维护的启用项中选择 ·
                    左侧选中一级分类, 右侧管理其二级分类 · ↑/↓/置顶 手动排序
                    (全站类别下拉与列表同序)
                </div>
            </div>

            {error && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    错误: {error}
                </div>
            )}
            {loading && <div className="text-sm text-slate-500">加载中…</div>}

            <div className="grid grid-cols-2 gap-4 items-start">
                <div className="card overflow-x-auto">
                    <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                        <div className="text-sm font-semibold">
                            一级分类
                            <span className="text-[11px] text-slate-400 font-normal ml-2">
                                共 {tree.length} 个
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <BatchActions
                                resource="category"
                                entityLabel="一级分类"
                                deleteKeyField="id"
                                buildDeleteData={(code) => ({
                                    id: Number(code),
                                })}
                                selected={cat1Sel.selectedKeys}
                                onClear={cat1Sel.clear}
                                onDone={load}
                            />
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={() => openCreate(0)}
                                hidden={!canCreate}
                            >
                                + 新增一级分类
                            </button>
                        </div>
                    </div>
                    {renderTable(tree, {
                        selectable: true,
                        emptyHint: '暂无一级分类',
                        sel: cat1Sel,
                    })}
                </div>

                <div className="card overflow-x-auto">
                    <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                        <div className="text-sm font-semibold">
                            {selected ? (
                                <>
                                    「{selected.nameZh} / {selected.nameEn}
                                    」的二级分类
                                    <span className="text-[11px] text-slate-400 font-normal ml-2">
                                        共 {selected.children.length} 个
                                    </span>
                                </>
                            ) : (
                                '二级分类'
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <BatchActions
                                resource="category"
                                entityLabel="二级分类"
                                deleteKeyField="id"
                                buildDeleteData={(code) => ({
                                    id: Number(code),
                                })}
                                selected={cat2Sel.selectedKeys}
                                onClear={cat2Sel.clear}
                                onDone={load}
                            />
                            {selected && (
                                <button
                                    type="button"
                                    className="btn-primary"
                                    onClick={() => openCreate(selected.id)}
                                    hidden={!canCreate}
                                >
                                    + 新增二级分类
                                </button>
                            )}
                        </div>
                    </div>
                    {selected ? (
                        renderTable(selected.children, {
                            emptyHint: '该分类下暂无二级分类',
                            sel: cat2Sel,
                        })
                    ) : (
                        <div className="px-4 py-8 text-center text-xs text-slate-400">
                            先在左侧选择一个一级分类
                        </div>
                    )}
                </div>
            </div>

            <Modal
                open={editor !== null}
                onClose={() => setEditor(null)}
                title={
                    editor?.target
                        ? `编辑${editor.parentId === 0 ? '一级' : '二级'}分类`
                        : editor?.parentId === 0
                          ? '新增一级分类'
                          : `新增二级分类 (${selected?.nameZh ?? ''})`
                }
                width={480}
            >
                {editor && (
                    <div className="p-5 space-y-4">
                        <div>
                            <label className="field-label">
                                <span className="text-rose-500">* </span>中文名
                            </label>
                            <input
                                className="inp"
                                value={editor.nameZh}
                                onChange={(e) =>
                                    setEditor({
                                        ...editor,
                                        nameZh: e.target.value,
                                    })
                                }
                                placeholder="例: 胶合板"
                            />
                        </div>
                        <div>
                            <label className="field-label">
                                <span className="text-rose-500">* </span>英文名
                            </label>
                            <input
                                className="inp font-mono"
                                value={editor.nameEn}
                                onChange={(e) =>
                                    setEditor({
                                        ...editor,
                                        nameEn: e.target.value,
                                        // 新增时编码默认跟随英文名, 手动改过编码后不再跟随
                                        ...(editor.codeTouched
                                            ? {}
                                            : { categoryCode: e.target.value }),
                                    })
                                }
                                placeholder="例: Plywood"
                            />
                            <div className="text-[11px] text-slate-400 mt-1">
                                展示用英文名, 可随时修改, 不影响材料与参数的关联.
                            </div>
                        </div>
                        <div>
                            <label className="field-label">
                                <span className="text-rose-500">* </span>类别编码
                            </label>
                            <input
                                className="inp font-mono disabled:bg-slate-50 disabled:text-slate-400"
                                value={editor.categoryCode}
                                disabled={editor.target !== null}
                                onChange={(e) =>
                                    setEditor({
                                        ...editor,
                                        categoryCode: e.target.value,
                                        codeTouched: true,
                                    })
                                }
                                placeholder="默认同英文名"
                            />
                            <div className="text-[11px] text-slate-400 mt-1">
                                业务键: 材料的类别1/类别2、喷墨参数的一级/二级类别存的都是它;
                                同级下唯一, 保存后不可修改.
                            </div>
                        </div>
                        {editor.parentId > 0 && (
                            <div>
                                <label className="field-label">
                                    材质编码前缀 (二级类别)
                                </label>
                                <input
                                    className="inp font-mono uppercase"
                                    value={editor.codePrefix}
                                    onChange={(e) =>
                                        setEditor({
                                            ...editor,
                                            codePrefix:
                                                e.target.value.toUpperCase(),
                                        })
                                    }
                                    placeholder="例: AB"
                                />
                                <div className="text-[11px] text-slate-400 mt-1">
                                    配置后, 新建材料选中本类别时自动生成编码
                                    (前缀+顺延编号, 例 AB012), 可手工修正.
                                </div>
                            </div>
                        )}
                        {editor.parentId > 0 && (
                            <div>
                                <label className="field-label">
                                    剔除机型 (已选{' '}
                                    {editor.excludedDeviceFamilies.length})
                                </label>
                                <CheckPickList
                                    items={familyOptions}
                                    keyOf={(f) => f.family}
                                    searchText={(f) =>
                                        `${f.family} ${f.names.join(' ')}`
                                    }
                                    render={(f) => (
                                        <span className="text-xs">
                                            <span className="font-mono">
                                                {f.family}
                                            </span>
                                            <span className="ml-2 text-slate-400">
                                                {f.names.length
                                                    ? f.names.join(' / ')
                                                    : '平台未接入'}
                                            </span>
                                        </span>
                                    )}
                                    selected={
                                        new Set(editor.excludedDeviceFamilies)
                                    }
                                    onChange={(next) =>
                                        setEditor({
                                            ...editor,
                                            excludedDeviceFamilies: familyOptions
                                                .map((f) => f.family)
                                                .filter((f) => next.has(f)),
                                        })
                                    }
                                    loading={familiesLoading}
                                    searchPlaceholder="搜索机型码 / 设备名"
                                    maxHeightClass="max-h-48"
                                />
                                <div className="text-[11px] text-slate-400 mt-1">
                                    勾选的机型在 Studio 材料库侧栏不展示本分类
                                    (含未发布设备); 不勾 = 全机型可见.
                                    只影响分类下发, 不影响材料本身.
                                </div>
                            </div>
                        )}
                        {editor.parentId === 0 && (
                            <div>
                                <label className="field-label">
                                    材料使用说明 (一级分类)
                                </label>
                                <input
                                    className="inp mb-2"
                                    value={editor.usageDocTitle}
                                    onChange={(e) =>
                                        setEditor({
                                            ...editor,
                                            usageDocTitle: e.target.value,
                                        })
                                    }
                                    placeholder="展示标题, 例: 金属材料加工指南"
                                />
                                <input
                                    className="inp"
                                    value={editor.usageDocUrl}
                                    onChange={(e) =>
                                        setEditor({
                                            ...editor,
                                            usageDocUrl: e.target.value,
                                        })
                                    }
                                    placeholder="https://… 教程/使用说明页面地址"
                                />
                                <div className="text-[11px] text-slate-400 mt-1">
                                    本类材料的默认使用说明 (标题 + 链接,
                                    成对生效);
                                    材料详情里单独配了链接的以材料为准.
                                </div>
                            </div>
                        )}
                        <div>
                            <label className="field-label">图标</label>
                            <ImageUploader
                                value={editor.icon}
                                onChange={(url) =>
                                    setEditor({ ...editor, icon: url })
                                }
                                size="md"
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                            <button
                                type="button"
                                className="btn-ghost"
                                onClick={() => setEditor(null)}
                                disabled={busy}
                            >
                                取消
                            </button>
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={onSaveEditor}
                                disabled={busy}
                            >
                                {busy ? '保存中…' : '保存'}
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}

export default CategoryAdmin;
